import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Slider } from "@/components/ui/slider";

import {
  ANUNCIO_ESTADOS,
  ANUNCIO_ORIGENES,
  ANUNCIO_VISIBILIDADES,
  ESTADOS_INMUEBLE,
  listBarrios,
  listCiudades,
  listDistritos,
  listComarcas,
  listProvincias,
  searchContactosLite,
} from "@/lib/anuncios.functions";
import { searchAgenciasLite } from "@/lib/agencias.functions";
import { listOperaciones, listTiposLocal, listLicencias, listUsosLocal } from "@/lib/datos.functions";
import { Check, ChevronsUpDown } from "lucide-react";

export type AnunciosFilters = {
  estados: string[];
  visibilidades: string[];
  origenes: string[];
  operacion_ids: string[];
  tipo_local_ids: string[];
  uso_local_ids: string[];
  licencia_ids: string[];
  rating_min: number | null;
  provincia_ids: string[];
  comarca_ids: string[];
  ciudad_ids: string[];
  distrito_ids: string[];
  barrio_ids: string[];
  cp: string;
  superficie_min: number | null;
  superficie_max: number | null;
  aseos_min: number | null;
  parking_plazas_min: number | null;
  estado_inmueble: string[];
  contacto_ids: string[];
  agencia_ids: string[];
  precio_alquiler_min: number | null;
  precio_alquiler_max: number | null;
  precio_traspaso_min: number | null;
  precio_traspaso_max: number | null;
  precio_venta_min: number | null;
  precio_venta_max: number | null;
  rent_bruta_min: number | null;
  rent_bruta_max: number | null;
};

export const emptyAnunciosFilters: AnunciosFilters = {
  estados: [],
  visibilidades: [],
  origenes: [],
  operacion_ids: [],
  tipo_local_ids: [],
  uso_local_ids: [],
  licencia_ids: [],
  rating_min: null,
  provincia_ids: [],
  comarca_ids: [],
  ciudad_ids: [],
  distrito_ids: [],
  barrio_ids: [],
  cp: "",
  superficie_min: null,
  superficie_max: null,
  aseos_min: null,
  parking_plazas_min: null,
  estado_inmueble: [],
  contacto_ids: [],
  agencia_ids: [],
  precio_alquiler_min: null,
  precio_alquiler_max: null,
  precio_traspaso_min: null,
  precio_traspaso_max: null,
  precio_venta_min: null,
  precio_venta_max: null,
  rent_bruta_min: null,
  rent_bruta_max: null,
};

const ESTADO_LABEL: Record<string, string> = {
  pendiente: "Pendiente",
  publicado: "Publicado",
  rechazado: "Rechazado",
  en_revision: "En revisión",
  borrado: "Borrado",
  no_publicado: "No publicado",
  demo: "Demo",
};
const VIS_LABEL: Record<string, string> = {
  publico: "Público",
  ciego: "Ciego",
  off_market: "Off market",
};
const ORIGEN_LABEL: Record<string, string> = {
  bizaclia: "Bizaclia",
  idealista: "Idealista",
  fotocasa: "Fotocasa",
  wallapop: "Wallapop",
  ai: "AI",
  otro: "Otro",
  alfainmo: "Alfa Inmobiliaria",
  kyero: "Kyero (XML)",
};
const ESTADO_INMUEBLE_LABEL: Record<string, string> = {
  obra_nueva: "Obra nueva",
  reformado: "Reformado",
  buen_estado: "Buen estado",
  por_reformar: "Por reformar",
  necesita_reforma: "Necesita reforma",
};

export function countActiveFilters(f: AnunciosFilters): number {
  let n = 0;
  for (const v of Object.values(f)) {
    if (Array.isArray(v)) {
      if (v.length) n += 1;
    } else if (typeof v === "string") {
      if (v.trim()) n += 1;
    } else if (v != null) {
      n += 1;
    }
  }
  return n;
}

export function serializeFilters(f: AnunciosFilters) {
  return {
    estados: f.estados.length ? (f.estados as never) : undefined,
    visibilidades: f.visibilidades.length ? (f.visibilidades as never) : undefined,
    origenes: f.origenes.length ? (f.origenes as never) : undefined,
    operacion_ids: f.operacion_ids.length ? f.operacion_ids : undefined,
    tipo_local_ids: f.tipo_local_ids.length ? f.tipo_local_ids : undefined,
    uso_local_ids: f.uso_local_ids.length ? f.uso_local_ids : undefined,
    licencia_ids: f.licencia_ids.length ? f.licencia_ids : undefined,
    rating_min: f.rating_min ?? undefined,
    provincia_ids: f.provincia_ids.length ? f.provincia_ids : undefined,
    comarca_ids: f.comarca_ids.length ? f.comarca_ids : undefined,
    ciudad_ids: f.ciudad_ids.length ? f.ciudad_ids : undefined,
    distrito_ids: f.distrito_ids.length ? f.distrito_ids : undefined,
    barrio_ids: f.barrio_ids.length ? f.barrio_ids : undefined,
    cp: f.cp.trim() || undefined,
    superficie_min: f.superficie_min ?? undefined,
    superficie_max: f.superficie_max ?? undefined,
    aseos_min: f.aseos_min ?? undefined,
    parking_plazas_min: f.parking_plazas_min ?? undefined,
    estado_inmueble: f.estado_inmueble.length ? (f.estado_inmueble as never) : undefined,
    contacto_ids: f.contacto_ids.length ? f.contacto_ids : undefined,
    agencia_ids: f.agencia_ids.length ? f.agencia_ids : undefined,
    precio_alquiler_min: f.precio_alquiler_min ?? undefined,
    precio_alquiler_max: f.precio_alquiler_max ?? undefined,
    precio_traspaso_min: f.precio_traspaso_min ?? undefined,
    precio_traspaso_max: f.precio_traspaso_max ?? undefined,
    precio_venta_min: f.precio_venta_min ?? undefined,
    precio_venta_max: f.precio_venta_max ?? undefined,
    rent_bruta_min: f.rent_bruta_min ?? undefined,
    rent_bruta_max: f.rent_bruta_max ?? undefined,
  };
}

export function MultiCheck<T extends string>({
  values,
  options,
  onChange,
  placeholder = "Selecciona…",
  searchPlaceholder = "Buscar…",
  searchValue,
  onSearchChange,
}: {
  values: string[];
  options: ReadonlyArray<{ value: T; label: string; search?: string }>;
  onChange: (next: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const toggle = (v: string) =>
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);

  const selectedLabels = options
    .filter((o) => values.includes(o.value))
    .map((o) => o.label);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal h-auto min-h-9 py-1.5"
        >
          <span className="flex flex-wrap gap-1 text-left">
            {selectedLabels.length === 0 ? (
              <span className="text-muted-foreground text-xs">{placeholder}</span>
            ) : (
              selectedLabels.map((l, i) => (
                <Badge key={i} variant="secondary" className="text-xs font-normal">
                  {l}
                </Badge>
              ))
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 z-[70]" align="start">
        <Command shouldFilter={!onSearchChange}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={onSearchChange}
          />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => {
                const checked = values.includes(o.value);
                return (
                  <CommandItem
                    key={o.value}
                    value={`${o.label} ${o.search ?? ""} ${o.value}`}
                    onSelect={() => toggle(o.value)}
                    className="cursor-pointer"
                  >
                    <Check className={cn("mr-2 h-4 w-4", checked ? "opacity-100" : "opacity-0")} />
                    <span className="truncate">{o.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function NumInput({
  value,
  onChange,
  placeholder,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
  placeholder?: string;
}) {
  return (
    <Input
      type="number"
      placeholder={placeholder}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
    />
  );
}

export function PriceRange({
  label,
  min,
  max,
  step,
  sliderMax,
  unit = "€",
  minPlaceholder,
  maxPlaceholder,
  onChange,
}: {
  label: string;
  min: number | null;
  max: number | null;
  step: number;
  sliderMax: number;
  unit?: string;
  minPlaceholder?: string;
  maxPlaceholder?: string;
  onChange: (next: { min: number | null; max: number | null }) => void;
}) {
  const lo = min ?? 0;
  const hi = max ?? sliderMax;
  const safeLo = Math.min(lo, sliderMax);
  const safeHi = Math.min(Math.max(hi, safeLo), sliderMax);
  return (
    <div className="grid gap-2">
      <Label className="text-xs">{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <Input
          type="number"
          placeholder={minPlaceholder ?? `Mín. ${unit}`}
          value={min ?? ""}
          onChange={(e) => onChange({ min: e.target.value === "" ? null : Number(e.target.value), max })}
        />
        <Input
          type="number"
          placeholder={maxPlaceholder ?? `Máx. ${unit}`}
          value={max ?? ""}
          onChange={(e) => onChange({ min, max: e.target.value === "" ? null : Number(e.target.value) })}
        />
      </div>
      <Slider
        value={[safeLo, safeHi]}
        min={0}
        max={sliderMax}
        step={step}
        onValueChange={(v) => onChange({ min: v[0] === 0 ? null : v[0], max: v[1] === sliderMax ? null : v[1] })}
        className="mt-1"
      />
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>0 {unit}</span>
        <span>{new Intl.NumberFormat("es-ES").format(sliderMax)} {unit}+</span>
      </div>
    </div>
  );
}

export function AnunciosFiltersForm({
  filters,
  onChange,
  hideInternal = false,
  hideComarca = false,
}: {
  filters: AnunciosFilters;
  onChange: (next: AnunciosFilters) => void;
  hideInternal?: boolean;
  /** Oculta el filtro por comarca (listados cuya consulta no lo admite). */
  hideComarca?: boolean;
}) {

  const patch = (p: Partial<AnunciosFilters>) => onChange({ ...filters, ...p });

  const fnProvincias = useServerFn(listProvincias);
  const fnComarcas = useServerFn(listComarcas);
  const fnCiudades = useServerFn(listCiudades);
  const fnDistritos = useServerFn(listDistritos);
  const fnBarrios = useServerFn(listBarrios);
  const fnContactos = useServerFn(searchContactosLite);
  const fnAgencias = useServerFn(searchAgenciasLite);
  const [contactoSearch, setContactoSearch] = useState("");
  const [debouncedContactoSearch, setDebouncedContactoSearch] = useState("");
  const [agenciaSearch, setAgenciaSearch] = useState("");
  const [debouncedAgenciaSearch, setDebouncedAgenciaSearch] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedContactoSearch(contactoSearch.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [contactoSearch]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedAgenciaSearch(agenciaSearch.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [agenciaSearch]);

  const ops = useQuery({ queryKey: ["operaciones"], queryFn: () => listOperaciones() as Promise<Array<{ id: string; nombre: string; slug?: string }>> });
  const tipos = useQuery({ queryKey: ["tipos_local_all"], queryFn: () => listTiposLocal({ data: {} }) as Promise<Array<{ id: string; nombre: string }>> });
  const usos = useQuery({ queryKey: ["usos_local_all"], queryFn: () => listUsosLocal({ data: {} }) as Promise<Array<{ id: string; nombre: string; tipo_local_id?: string }>> });
  const lics = useQuery({ queryKey: ["licencias"], queryFn: () => listLicencias() as Promise<{ items: Array<{ id: string; nombre: string }> }> });
  const provs = useQuery({ queryKey: ["provincias"], queryFn: () => fnProvincias() as Promise<Array<{ id: string; nombre: string }>> });
  const comarcas = useQuery({ queryKey: ["comarcas_all"], queryFn: () => fnComarcas() as Promise<Array<{ id: string; nombre: string; provincia_id: string }>>, enabled: !hideComarca });
  const ciudades = useQuery({ queryKey: ["ciudades_all"], queryFn: () => fnCiudades({ data: {} }) as Promise<Array<{ id: string; nombre: string; provincia_id?: string; comarca_id?: string | null }>> });
  const distritos = useQuery({ queryKey: ["distritos_all"], queryFn: () => fnDistritos({ data: {} }) as Promise<Array<{ id: string; nombre: string; ciudad_id?: string }>> });
  const barrios = useQuery({ queryKey: ["barrios_all"], queryFn: () => fnBarrios({ data: {} }) as Promise<Array<{ id: string; nombre: string; distrito_id?: string }>> });
  const contactos = useQuery({
    queryKey: ["contactos_lite", debouncedContactoSearch, filters.contacto_ids],
    queryFn: () => fnContactos({ data: { search: debouncedContactoSearch, ids: filters.contacto_ids, limit: 10 } }) as Promise<Array<{ id: string; nombre: string; apellidos?: string | null; email?: string | null; telefono?: string | null; ciudad?: string | null }>>,
    enabled: !hideInternal,
  });
  const agencias = useQuery({
    queryKey: ["agencias_lite", debouncedAgenciaSearch, filters.agencia_ids],
    queryFn: () => fnAgencias({ data: { search: debouncedAgenciaSearch, ids: filters.agencia_ids, limit: 10 } }) as Promise<Array<{ id: string; nombre: string; email?: string | null; telefono?: string | null; region?: string | null }>>,
    enabled: !hideInternal,
  });

  const filteredComarcas = useMemo(
    () => (filters.provincia_ids.length
      ? (comarcas.data ?? []).filter((k) => filters.provincia_ids.includes(k.provincia_id))
      : (comarcas.data ?? [])),
    [comarcas.data, filters.provincia_ids],
  );
  const filteredCiudades = useMemo(
    () => (ciudades.data ?? []).filter(
      (c) =>
        (!filters.provincia_ids.length || (c.provincia_id && filters.provincia_ids.includes(c.provincia_id))) &&
        (!filters.comarca_ids.length || (c.comarca_id && filters.comarca_ids.includes(c.comarca_id))),
    ),
    [ciudades.data, filters.provincia_ids, filters.comarca_ids],
  );
  const filteredDistritos = useMemo(
    () => (filters.ciudad_ids.length
      ? (distritos.data ?? []).filter((d) => d.ciudad_id && filters.ciudad_ids.includes(d.ciudad_id))
      : (distritos.data ?? [])),
    [distritos.data, filters.ciudad_ids],
  );
  const filteredBarrios = useMemo(
    () => (filters.distrito_ids.length
      ? (barrios.data ?? []).filter((b) => b.distrito_id && filters.distrito_ids.includes(b.distrito_id))
      : (barrios.data ?? [])),
    [barrios.data, filters.distrito_ids],
  );

  const filteredUsos = useMemo(
    () => (filters.tipo_local_ids.length
      ? (usos.data ?? []).filter((u) => u.tipo_local_id && filters.tipo_local_ids.includes(u.tipo_local_id))
      : (usos.data ?? [])),
    [usos.data, filters.tipo_local_ids],
  );

  const selectedOpSlugs = useMemo(() => {
    if (!filters.operacion_ids.length) return new Set<string>(["alquiler", "traspaso", "venta", "rentabilidad"]);
    return new Set(
      (ops.data ?? [])
        .filter((o) => filters.operacion_ids.includes(o.id))
        .map((o) => o.slug ?? ""),
    );
  }, [ops.data, filters.operacion_ids]);
  const showAlquiler = selectedOpSlugs.has("alquiler") || selectedOpSlugs.has("traspaso") || selectedOpSlugs.has("rentabilidad");
  const showTraspaso = selectedOpSlugs.has("traspaso");
  const showVenta = selectedOpSlugs.has("venta") || selectedOpSlugs.has("rentabilidad");
  const showRentabilidad = selectedOpSlugs.has("rentabilidad");

  return (
    <Accordion type="multiple" defaultValue={["general", "direccion", "caracteristicas", "contactos", "agencia"]} className="w-full">
      <AccordionItem value="general">
        <AccordionTrigger>Generales</AccordionTrigger>
        <AccordionContent className="space-y-3">
          {!hideInternal && (
            <div>
              <Label className="text-xs">Estado</Label>
              <MultiCheck
                values={filters.estados}
                options={ANUNCIO_ESTADOS.map((e) => ({ value: e, label: ESTADO_LABEL[e] }))}
                onChange={(v) => patch({ estados: v })}
              />
            </div>
          )}
          {!hideInternal && (
            <div>
              <Label className="text-xs">Visibilidad</Label>
              <MultiCheck
                values={filters.visibilidades}
                options={ANUNCIO_VISIBILIDADES.map((v) => ({ value: v, label: VIS_LABEL[v] }))}
                onChange={(v) => patch({ visibilidades: v })}
              />
            </div>
          )}
          {!hideInternal && (
            <div>
              <Label className="text-xs">Origen</Label>
              <MultiCheck
                values={filters.origenes}
                options={ANUNCIO_ORIGENES.map((o) => ({ value: o, label: ORIGEN_LABEL[o] }))}
                onChange={(v) => patch({ origenes: v })}
              />
            </div>
          )}
          <div>
            <Label className="text-xs">Operación</Label>
            <MultiCheck
              values={filters.operacion_ids}
              options={(ops.data ?? []).map((o) => ({ value: o.id, label: o.nombre }))}
              onChange={(v) => patch({ operacion_ids: v })}
            />
          </div>
          <div>
            <Label className="text-xs">Tipo de local</Label>
            <MultiCheck
              values={filters.tipo_local_ids}
              options={(tipos.data ?? []).map((o) => ({ value: o.id, label: o.nombre }))}
              onChange={(v) => patch({ tipo_local_ids: v, uso_local_ids: [] })}
            />
          </div>
          <div>
            <Label className="text-xs">Uso del local</Label>
            <MultiCheck
              values={filters.uso_local_ids}
              options={filteredUsos.map((o) => ({ value: o.id, label: o.nombre }))}
              onChange={(v) => patch({ uso_local_ids: v })}
            />
          </div>
          <div>
            <Label className="text-xs">Licencia</Label>
            <MultiCheck
              values={filters.licencia_ids}
              options={(lics.data?.items ?? []).map((o) => ({ value: o.id, label: o.nombre }))}
              onChange={(v) => patch({ licencia_ids: v })}
            />
          </div>
          {!hideInternal && (
            <div>
              <Label className="text-xs">Rating mínimo</Label>
              <NumInput value={filters.rating_min} onChange={(v) => patch({ rating_min: v == null ? null : Math.min(5, Math.max(1, v)) })} placeholder="1-5" />
            </div>
          )}
          {showAlquiler && (
            <PriceRange
              label="Precio de alquiler (€/mes)"
              min={filters.precio_alquiler_min}
              max={filters.precio_alquiler_max}
              step={50}
              sliderMax={10000}
              onChange={(v) => patch({ precio_alquiler_min: v.min, precio_alquiler_max: v.max })}
            />
          )}
          {showTraspaso && (
            <PriceRange
              label="Precio de traspaso (€)"
              min={filters.precio_traspaso_min}
              max={filters.precio_traspaso_max}
              step={1000}
              sliderMax={500000}
              onChange={(v) => patch({ precio_traspaso_min: v.min, precio_traspaso_max: v.max })}
            />
          )}
          {showVenta && (
            <PriceRange
              label="Precio de venta (€)"
              min={filters.precio_venta_min}
              max={filters.precio_venta_max}
              step={5000}
              sliderMax={3000000}
              onChange={(v) => patch({ precio_venta_min: v.min, precio_venta_max: v.max })}
            />
          )}
          {showRentabilidad && (
            <PriceRange
              label="Rentabilidad bruta (%)"
              min={filters.rent_bruta_min}
              max={filters.rent_bruta_max}
              step={0.1}
              sliderMax={20}
              unit="%"
              onChange={(v) => patch({ rent_bruta_min: v.min, rent_bruta_max: v.max })}
            />
          )}
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="direccion">
        <AccordionTrigger>Dirección</AccordionTrigger>
        <AccordionContent className="space-y-3">
          <div>
            <Label className="text-xs">Provincia</Label>
            <MultiCheck
              values={filters.provincia_ids}
              options={(provs.data ?? []).map((o) => ({ value: o.id, label: o.nombre }))}
              onChange={(v) => patch({ provincia_ids: v, comarca_ids: [], ciudad_ids: [], distrito_ids: [], barrio_ids: [] })}
            />
          </div>
          {!hideComarca && (
            <div>
              <Label className="text-xs">Comarca</Label>
              <MultiCheck
                values={filters.comarca_ids}
                options={filteredComarcas.map((o) => ({ value: o.id, label: o.nombre }))}
                onChange={(v) => patch({ comarca_ids: v, ciudad_ids: [], distrito_ids: [], barrio_ids: [] })}
              />
            </div>
          )}
          <div>
            <Label className="text-xs">Ciudad</Label>
            <MultiCheck
              values={filters.ciudad_ids}
              options={filteredCiudades.map((o) => ({ value: o.id, label: o.nombre }))}
              onChange={(v) => patch({ ciudad_ids: v, distrito_ids: [], barrio_ids: [] })}
            />
          </div>
          <div>
            <Label className="text-xs">Distrito</Label>
            <MultiCheck
              values={filters.distrito_ids}
              options={filteredDistritos.map((o) => ({ value: o.id, label: o.nombre }))}
              onChange={(v) => patch({ distrito_ids: v, barrio_ids: [] })}
            />
          </div>
          <div>
            <Label className="text-xs">Barrio</Label>
            <MultiCheck
              values={filters.barrio_ids}
              options={filteredBarrios.map((o) => ({ value: o.id, label: o.nombre }))}
              onChange={(v) => patch({ barrio_ids: v })}
            />
          </div>
          <div>
            <Label className="text-xs">Código postal</Label>
            <Input
              placeholder="CP..."
              value={filters.cp}
              onChange={(e) => patch({ cp: e.target.value })}
            />
          </div>
        </AccordionContent>
      </AccordionItem>

      <AccordionItem value="caracteristicas">
        <AccordionTrigger>Características</AccordionTrigger>
        <AccordionContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Superficie mín. (m²)</Label>
              <NumInput value={filters.superficie_min} onChange={(v) => patch({ superficie_min: v })} />
            </div>
            <div>
              <Label className="text-xs">Superficie máx. (m²)</Label>
              <NumInput value={filters.superficie_max} onChange={(v) => patch({ superficie_max: v })} />
            </div>
            <div>
              <Label className="text-xs">Nº aseos mín.</Label>
              <NumInput value={filters.aseos_min} onChange={(v) => patch({ aseos_min: v })} />
            </div>
            <div>
              <Label className="text-xs">Plazas parking mín.</Label>
              <NumInput value={filters.parking_plazas_min} onChange={(v) => patch({ parking_plazas_min: v })} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Estado del inmueble</Label>
            <MultiCheck
              values={filters.estado_inmueble}
              options={ESTADOS_INMUEBLE.map((o) => ({ value: o, label: ESTADO_INMUEBLE_LABEL[o] }))}
              onChange={(v) => patch({ estado_inmueble: v })}
            />
          </div>
        </AccordionContent>
      </AccordionItem>

      {!hideInternal && (
        <AccordionItem value="contactos">
          <AccordionTrigger>Contactos</AccordionTrigger>
          <AccordionContent>
            <MultiCheck
              values={filters.contacto_ids}
              options={(contactos.data ?? []).map((c) => ({
                value: c.id,
                label: [c.nombre, c.apellidos].filter(Boolean).join(" ") || c.email || "—",
                search: [c.email, c.telefono, c.ciudad].filter(Boolean).join(" "),
              }))}
              onChange={(v) => patch({ contacto_ids: v })}
              searchValue={contactoSearch}
              onSearchChange={setContactoSearch}
            />
          </AccordionContent>
        </AccordionItem>
      )}

      {!hideInternal && (
        <AccordionItem value="agencia">
          <AccordionTrigger>Agencia</AccordionTrigger>
          <AccordionContent>
            <MultiCheck
              values={filters.agencia_ids}
              options={(agencias.data ?? []).map((a) => ({
                value: a.id,
                label: a.nombre,
                search: [a.email, a.telefono, a.region].filter(Boolean).join(" "),
              }))}
              onChange={(v) => patch({ agencia_ids: v })}
              searchValue={agenciaSearch}
              onSearchChange={setAgenciaSearch}
            />
          </AccordionContent>
        </AccordionItem>
      )}

    </Accordion>
  );
}
